
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import IconArrowBack from '@/components/svg/IconArrowBack.vue'
import IconCamera from '@/components/svg/IconCamera.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import {
  CURRENT_CAMERA_KEY,
  LOCAL_STORAGE_ITEM,
  OCR_BLUR_LADDER_PX,
  OCR_GOOD_CONFIDENCE,
  OCR_MAX_IMAGE_SIZE,
  OCR_STILL_PHOTO_TIMEOUT_MS,
} from '@/utils/constants'
import {
  Cv,
  findDocumentQuad,
  isStableQuad,
  loadOpenCv,
  mapVideoPointToStill,
  Pt,
  warpDocument,
} from '@/utils/documentScanner'
import errorHandler from '@/utils/errorHandler'
import { getMobileOS, promptVideoPermission, stopStreams } from '@/utils/helpers'
import { blurCanvas } from '@/utils/imageProcess'
import mitt from '@/utils/mitt'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { isEmpty } from 'smartbarcode-web-core/src/utils/typeChecker'
import { TError } from 'smartbarcode-web-core/src/utils/types/index'
import { createWorker, OEM, Worker } from 'tesseract.js'
import { mixins, Options } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import BulkScanSwitchButton from './common/BulkScanSwitchButton.vue'
import BarcodeReaderMixin from './mixins/BarcodeReaderMixin.vue'

// blank sub-languages so jpn does not also try to load jpn_vert;
// jpn keeps only real gaps (no spaces between CJK characters), eng keeps single spaces
const ocrWorkerConfig = (lang: 'eng' | 'jpn') =>
  `tessedit_load_sublangs \npreserve_interword_spaces ${lang === 'jpn' ? 1 : 0}\n`

const OUTLINE_INTERVAL_MS = 100
const OUTLINE_FRAME_WIDTH = 320
// detections without a quad before the outline disappears: handheld motion blur drops roughly a third of the
// detections, so the outline holds about 600 ms (6 ticks) instead of blinking
const OUTLINE_MAX_MISSES = 6
const OUTLINE_STABLE_PX = 6
const OUTLINE_SMOOTH_PX = 20
// snapshot() warps only when the latest detection found the quad and it held still for this many detections
const OUTLINE_WARP_STABLE_FRAMES = 3
// outside the component data on purpose: Vue would wrap the huge OpenCV module in a reactive proxy
let openCv: Cv | null = null

// ImageCapture is missing from TS 3.9 lib.dom
type TImageCaptureCtor = new (track: MediaStreamTrack) => { takePhoto(): Promise<Blob> }
const ImageCaptureCtor = (window as unknown as { ImageCapture?: TImageCaptureCtor }).ImageCapture

@Options({
  components: {
    IconCamera,
    IconArrowBack,
    IconSwitch,
    SelectionCameraPopup,
    BulkScanSwitchButton,
  },
  emits: ['update:modelValue', 'update:confirm', 'update:img', 'close'],
  name: 'OCRComponent',
})
export default class OCRComponent extends mixins(BarcodeReaderMixin) {
  @Prop({ type: String }) readonly fieldName!: string
  @Prop({ type: [String, Number] }) readonly initialValue?: string | number
  @Prop({ type: Boolean }) readonly isShowMultipleScan?: boolean

  isRecognizing = false
  isRecognized = true
  isCapturing = false
  loading = true
  // one above the dialog overlay that opened us; later popups (confirm, camera list) get higher ones
  layerZIndex = Math.max(0, ...Array.from(document.querySelectorAll('.el-overlay')).map((el) => Number(getComputedStyle(el).zIndex) || 0)) + 1
  video: HTMLVideoElement | null = null
  pictureWidth = 320
  pictureHeight = 320
  isCaptured = false
  isCropperReady = false
  croppedImg = {} as HTMLCanvasElement
  img = ''
  cropper = {} as Cropper
  worker = {} as Worker
  workerReady: Promise<unknown> = Promise.resolve()
  ocrLanguage: 'eng' | 'jpn' = localStorage.getItem(LOCAL_STORAGE_ITEM.OCR_LANGUAGE) === 'jpn' ? 'jpn' : 'eng'
  isSwitchingLanguage = false
  outlineFrame = document.createElement('canvas')
  outlineQuad: Pt[] | null = null
  outlineTimer = 0
  outlineLastRun = 0
  outlineMisses = 0
  outlineStableFrames = 0

  @Watch('selectedCamera')
  async setCameraId() {
    this.isOpenDialog = false
    this.vidOff()
    localStorage.setItem(CURRENT_CAMERA_KEY, this.selectedCamera)
    await promptVideoPermission()
    await this.setupVideo()
  }

  onEmitCapturedImage() {
    mitt.emit('update:imageOCR', { [this.fieldName]: this.img })
  }

  confirm() {
    this.vidOff()
    this.onEmitCapturedImage()
    this.$emit('update:confirm', this.valueCode.trim())
  }

  async reTakePicture() {
    if (this.valueCode === '') {
      await this.handleReTakePicture()
      return
    }
    this.$confirm(this.$t('confirm_reset_scanned_value'), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      cancelButtonText: this.$t('cancel'),
      type: 'warning',
    }).then(async () => {
      await this.handleReTakePicture()
    })
  }

  async handleReTakePicture() {
    this.isCaptured = false
    this.$emit('update:img', false)
    this.valueCode = ''
    this.img = ''
    this.cropper.destroy()
    await this.setupVideo()
  }

  async getCropData() {
    try {
      this.croppedImg = this.cropper.getCroppedCanvas({
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      })
      if (!this.croppedImg) return
      if (this.isRecognized) return
      if (!this.croppedImg) return

      await this.onRecognize()
    } catch (error) {
      console.error(error)
    }
  }

  async selectOcrLanguage(lang: 'eng' | 'jpn') {
    if (lang === this.ocrLanguage || this.isRecognizing || this.isSwitchingLanguage) return
    this.ocrLanguage = lang
    localStorage.setItem(LOCAL_STORAGE_ITEM.OCR_LANGUAGE, lang)
    this.isSwitchingLanguage = true
    try {
      await this.workerReady
      await this.worker.reinitialize(this.ocrLanguage, OEM.LSTM_ONLY, ocrWorkerConfig(this.ocrLanguage))
      if (this.isCaptured && !isEmpty(this.cropper.getCropBoxData())) await this.getCropData()
    } catch (e) {
      errorHandler(e as TError)
    } finally {
      this.isSwitchingLanguage = false
    }
  }

  // paper usually passes on the first, unblurred try; each retry hides a finer screen pixel grid, best confidence wins
  async recognizeBest(source: HTMLCanvasElement) {
    let best = { text: '', confidence: -1 }
    for (const radius of OCR_BLUR_LADDER_PX) {
      const { data } = await this.worker.recognize(radius ? blurCanvas(source, radius) : source)
      if (data.confidence > best.confidence) best = data
      if (best.confidence >= OCR_GOOD_CONFIDENCE) break
    }
    return best.text
  }

  async onRecognize() {
    this.isRecognizing = true
    try {
      await this.workerReady
      const text = await this.recognizeBest(this.croppedImg)
      if (this.isShowMultipleScan && this.isMultipleScan) {
        this.handleMultipleScan(text)
        return
      }
      this.valueCode = text
    } catch (e) {
      errorHandler(e as TError)
    } finally {
      this.isRecognizing = false
    }
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }

    const contentDialog = this.getContentDialog(plainText)
    this.isHandleMultipleScan = true

    this.$confirm(contentDialog, this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
      closeOnClickModal: true,
      showCancelButton: true,
      dangerouslyUseHTMLString: true,
    })
      .then(() => {
        this.valueCode = this.processValueCode(plainText)
      })
      .finally(() => {
        this.isHandleMultipleScan = false
      })
  }

  processValueCode(value: string) {
    let result = ''
    if (this.valueCode.trim() !== '') {
      result = `${this.valueCode.trim()} ${value}`
    } else {
      result = value
    }
    return result.trim()
  }

  beforeCreate() {
    this.workerReady = new Promise<Worker>((resolve, reject) => {
      // without errorHandler, a failed traineddata download leaves createWorker pending forever
      createWorker(['eng', 'jpn'], OEM.LSTM_ONLY, { errorHandler: reject }, ocrWorkerConfig(this.ocrLanguage)).then(resolve, reject)
    }).then((worker) => {
      this.worker = worker
      return worker.reinitialize(this.ocrLanguage, OEM.LSTM_ONLY, ocrWorkerConfig(this.ocrLanguage))
    })
    this.workerReady.catch((e) => errorHandler(e as TError))
  }

  async beforeUnmount() {
    // terminate once created, even if the startup reinitialize failed
    this.workerReady.finally(() => ('terminate' in this.worker ? this.worker.terminate() : undefined)).catch(() => undefined)
    if (!isEmpty(this.cropper)) {
      // eslint-disable-next-line no-unused-expressions
      this.cropper?.clear()
      // eslint-disable-next-line no-unused-expressions
      this.cropper?.reset()
      // eslint-disable-next-line no-unused-expressions
      this.cropper?.destroy()
    }
    if (this.img) {
      URL.revokeObjectURL(this.img)
    }

    this.vidOff()
  }

  async mounted() {
    // Set initial value if provided
    if (this.initialValue) {
      this.valueCode = this.initialValue.toString()
    }

    // the outline is wanted as soon as the camera shows, long before the first recognize: do not wait for the OCR worker
    loadOpenCv()
      .then((cv) => this.onOpenCvLoaded(cv))
      .catch(() => undefined)

    await this.initCameraSelection()
    await promptVideoPermission()
    await this.setupVideo()
  }

  get guideText() {
    return this.isCaptured
      ? isEmpty(this.valueCode)
        ? 'please_select_ocr_text'
        : 'please_confirm_ocr_text'
      : 'please_take_ocr_area'
  }

  startCrop() {
    const image = document.getElementById('imgTag') as HTMLImageElement
    if (!image) return
    this.cropper = new Cropper(image, {
      // the JPEG comes from our own canvas: no EXIF orientation to read back out of the data URL
      checkOrientation: false,
      center: false,
      highlight: false,
      viewMode: 3,
      autoCrop: false,
      background: false,
      autoCropArea: 1,
      // Cropper paints its own copy of the image and hides the original at once; a large still flashes black while
      // that copy is still decoding, so the original stays visible underneath until the copy can paint
      ready: () => {
        const copy = image.parentElement?.querySelector<HTMLImageElement>('.cropper-canvas > img')
        const painted = copy ? copy.decode().catch(() => undefined) : Promise.resolve()
        painted.then(() => {
          this.isCropperReady = true
        })
      },
      cropend: async () => {
        this.isRecognized = false
        await this.getCropData()
      },
    })
  }

  async vidOff() {
    this.stopOutline()
    stopStreams(this.activeStreams)
  }

  onOpenCvLoaded(cv: Cv | null) {
    if (!cv || !this.componentMounted) return
    openCv = cv
    if (this.activeStreams.length) this.startOutline()
  }

  startOutline() {
    this.stopOutline()
    if (!openCv || this.isCaptured) return
    this.outlineTimer = requestAnimationFrame((now) => this.tickOutline(now))
  }

  stopOutline() {
    cancelAnimationFrame(this.outlineTimer)
    this.outlineTimer = 0
    this.outlineQuad = null
    this.outlineMisses = 0
    this.outlineStableFrames = 0
    this.drawOutline()
  }

  tickOutline(now: number) {
    this.outlineTimer = requestAnimationFrame((time) => this.tickOutline(time))
    if (now - this.outlineLastRun < OUTLINE_INTERVAL_MS || document.visibilityState === 'hidden') return
    this.outlineLastRun = now
    this.detectOutline()
  }

  detectOutline() {
    const video = this.video
    const frame = this.outlineFrame
    if (!openCv || !video || video.readyState < 2 || !video.videoWidth) return
    const height = Math.round((OUTLINE_FRAME_WIDTH * video.videoHeight) / video.videoWidth)
    if (frame.width !== OUTLINE_FRAME_WIDTH || frame.height !== height) {
      frame.width = OUTLINE_FRAME_WIDTH
      frame.height = height
    }
    const context = frame.getContext('2d', { willReadFrequently: true })
    if (!context) return
    context.drawImage(video, 0, 0, frame.width, frame.height)
    try {
      this.updateOutline(findDocumentQuad(openCv, context.getImageData(0, 0, frame.width, frame.height)))
    } catch {
      // OpenCV failed at runtime: leave the plain camera preview
      openCv = null
      this.stopOutline()
    }
  }

  updateOutline(found: Pt[] | null) {
    const prev = this.outlineQuad
    if (found) {
      this.outlineMisses = 0
      this.outlineStableFrames = isStableQuad(prev, found, OUTLINE_STABLE_PX) ? this.outlineStableFrames + 1 : 0
      this.outlineQuad =
        prev && isStableQuad(prev, found, OUTLINE_SMOOTH_PX)
          ? found.map((p, i) => ({ x: (prev[i].x + p.x) / 2, y: (prev[i].y + p.y) / 2 }))
          : found
    } else {
      this.outlineMisses += 1
      if (this.outlineMisses < OUTLINE_MAX_MISSES) return
      this.outlineQuad = null
      this.outlineStableFrames = 0
    }
    this.drawOutline()
  }

  // the video is object-fit: cover, so frame coordinates are scaled and centered like the video itself
  drawOutline() {
    const canvas = this.$refs.outline as HTMLCanvasElement | undefined
    const context = canvas?.getContext('2d')
    if (!canvas || !context || !canvas.clientWidth) return
    const dpr = window.devicePixelRatio || 1
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    canvas.width = Math.round(width * dpr)
    canvas.height = Math.round(height * dpr)
    const quad = this.outlineQuad
    if (!quad) return
    const scale = Math.max(width / this.outlineFrame.width, height / this.outlineFrame.height)
    const offsetX = (width - this.outlineFrame.width * scale) / 2
    const offsetY = (height - this.outlineFrame.height * scale) / 2
    context.scale(dpr, dpr)
    context.beginPath()
    context.moveTo(quad[0].x * scale + offsetX, quad[0].y * scale + offsetY)
    quad.slice(1).forEach((p) => context.lineTo(p.x * scale + offsetX, p.y * scale + offsetY))
    context.closePath()
    context.fillStyle = 'rgba(30, 107, 255, 0.2)'
    context.fill()
    context.lineWidth = 3
    context.lineJoin = 'round'
    context.strokeStyle = '#1e6bff'
    context.stroke()
  }

  // `quad` is in outline-frame pixels; `video` is the preview size the outline was detected on
  documentCanvas(source: HTMLCanvasElement, quad: Pt[] | null, video: { width: number, height: number }) {
    if (!quad || !openCv) return source
    const { width, height } = this.outlineFrame
    try {
      return warpDocument(
        openCv,
        source,
        quad.map((p) =>
          mapVideoPointToStill({ x: (p.x * video.width) / width, y: (p.y * video.height) / height }, video, source)
        )
      )
    } catch {
      // fall back to the raw frame
      return source
    }
  }

  // long edge at most OCR_MAX_IMAGE_SIZE
  fitImage(source: HTMLCanvasElement) {
    const scale = Math.min(1, OCR_MAX_IMAGE_SIZE / Math.max(source.width, source.height))
    if (scale === 1) return source
    const out = document.createElement('canvas')
    out.width = Math.round(source.width * scale)
    out.height = Math.round(source.height * scale)
    const context = out.getContext('2d')
    if (context) context.drawImage(source, 0, 0, out.width, out.height)
    return out
  }

  // full-sensor still when the browser has ImageCapture; times out so a stalled takePhoto() never blocks the shutter
  async takeStillPhoto(): Promise<ImageBitmap | null> {
    const track = (this.video?.srcObject as MediaStream | null)?.getVideoTracks()[0]
    // phones only: desktop webcams stall in takePhoto() until the timeout
    if (!track || !ImageCaptureCtor || getMobileOS() === 'Other') return null
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), OCR_STILL_PHOTO_TIMEOUT_MS))
    try {
      const blob = await Promise.race([new ImageCaptureCtor(track).takePhoto(), timeout])
      return blob ? await createImageBitmap(blob) : null
    } catch {
      return null
    }
  }

  resetScannedValue(isShowConfirmation = false) {
    if (!isShowConfirmation) {
      this.valueCode = ''
      return
    }
    this.$confirm(this.$t('confirm_reset_scanned_value'), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      cancelButtonText: this.$t('cancel'),
      type: 'warning',
    }).then(() => {
      this.valueCode = ''
    })
  }

  async snapshot() {
    if (this.isCapturing || !this.video) return
    // frozen before the await: the outline keeps moving while takePhoto() runs
    const quad = this.outlineMisses === 0 && this.outlineStableFrames >= OUTLINE_WARP_STABLE_FRAMES ? this.outlineQuad : null
    const video = { width: this.video.videoWidth || this.pictureWidth, height: this.video.videoHeight || this.pictureHeight }
    this.isCapturing = true
    // shutter: the preview freezes at once and stays until the still replaces it; retake restarts the stream
    this.video.pause()
    try {
      const still = await this.takeStillPhoto()
      // closed during takePhoto(): the lookups below would hit another component's DOM
      if (!this.componentMounted) {
        if (still) still.close()
        return
      }
      const canvas = this.$refs.snapshot as HTMLCanvasElement | undefined
      const context = canvas?.getContext('2d')
      if (!canvas || !context) return
      canvas.width = still?.width ?? video.width
      canvas.height = still?.height ?? video.height
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(still ?? this.video, 0, 0, canvas.width, canvas.height)
      if (still) still.close()
      this.img = this.fitImage(this.documentCanvas(canvas, quad, video)).toDataURL('image/jpeg')
      // swap only once the still is decoded, so the frozen preview goes straight to the picture
      await this.$nextTick()
      await (document.getElementById('imgTag') as HTMLImageElement | null)?.decode().catch(() => undefined)
      this.isCropperReady = false
      this.isCaptured = true
      this.$emit('update:img', true)
      this.vidOff()
      await this.$nextTick()
      this.startCrop()
    } finally {
      this.isCapturing = false
    }
  }

  async takeMoreSnapshot() {
    this.isCaptured = false
    this.$emit('update:img', false)
    this.img = ''
    this.cropper.destroy()
    await this.setupVideo()
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            width: { ideal: 3000 },
            height: { ideal: 3000 },
            frameRate: {
              ideal: 30,
              max: 60,
            },
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 3000 },
            height: { ideal: 3000 },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }

  async setStream(stream: MediaStream) {
    this.activeStreams.push(stream)
    // Setup the video stream
    if (this.video) {
      this.video.srcObject = stream
      // snapshot() pauses the element; play() covers browsers whose autoplay does not restart a swapped stream
      this.video.play().catch(() => undefined)
      const streamSetting = stream.getVideoTracks()[0].getSettings()
      // actual width & height of the camera video
      this.pictureWidth = streamSetting.width || this.pictureWidth
      this.pictureHeight = streamSetting.height || this.pictureHeight
    }
    this.startOutline()
  }
}
